"use client";

import { useState } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------ */
/*  Salary data (hardcoded UK estimates, GBP)                         */
/* ------------------------------------------------------------------ */

type Role =
  | "ai-engineer"
  | "data-scientist"
  | "ml-engineer"
  | "prompt-engineer"
  | "ai-product-manager";

type Experience = "entry" | "mid" | "senior" | "lead";

type Region =
  | "london"
  | "south-east"
  | "midlands"
  | "north"
  | "scotland"
  | "wales"
  | "remote";

/** Base mid-level London salaries (min/max) for each role. */
const BASE_SALARIES: Record<Role, { min: number; max: number }> = {
  "ai-engineer": { min: 60000, max: 95000 },
  "data-scientist": { min: 50000, max: 80000 },
  "ml-engineer": { min: 58000, max: 92000 },
  "prompt-engineer": { min: 45000, max: 72000 },
  "ai-product-manager": { min: 62000, max: 98000 },
};

/** Multiplier applied to base salary by experience level. */
const EXPERIENCE_MULTIPLIER: Record<Experience, number> = {
  entry: 0.7,
  mid: 1.0,
  senior: 1.3,
  lead: 1.55,
};

/** Multiplier applied to base salary by region. */
const REGION_MULTIPLIER: Record<Region, number> = {
  london: 1.0,
  "south-east": 0.9,
  midlands: 0.82,
  north: 0.8,
  scotland: 0.82,
  wales: 0.78,
  remote: 0.88,
};

const ROLES: { value: Role; label: string }[] = [
  { value: "ai-engineer", label: "AI Engineer" },
  { value: "data-scientist", label: "Data Scientist" },
  { value: "ml-engineer", label: "ML Engineer" },
  { value: "prompt-engineer", label: "Prompt Engineer" },
  { value: "ai-product-manager", label: "AI Product Manager" },
];

const EXPERIENCE_LEVELS: { value: Experience; label: string }[] = [
  { value: "entry", label: "Entry Level" },
  { value: "mid", label: "Mid Level" },
  { value: "senior", label: "Senior" },
  { value: "lead", label: "Lead" },
];

const REGIONS: { value: Region; label: string }[] = [
  { value: "london", label: "London" },
  { value: "south-east", label: "South East" },
  { value: "midlands", label: "Midlands" },
  { value: "north", label: "North" },
  { value: "scotland", label: "Scotland" },
  { value: "wales", label: "Wales" },
  { value: "remote", label: "Remote" },
];

function formatGBP(value: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(value);
}

function roundToNearest(value: number, nearest: number): number {
  return Math.round(value / nearest) * nearest;
}

/* ------------------------------------------------------------------ */
/*  Select field — real <label htmlFor>, brass focus ring             */
/* ------------------------------------------------------------------ */

interface SelectFieldProps<T extends string> {
  id: string;
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}

function SelectField<T extends string>({
  id,
  label,
  value,
  options,
  onChange,
}: SelectFieldProps<T>) {
  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className="text-xs font-semibold uppercase tracking-[0.12em] text-mid"
      >
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className={cn(
          "min-h-[44px] w-full rounded-md border border-line bg-paper px-3.5 py-2.5",
          "text-base text-dark transition-colors duration-150 hover:border-mid/50",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary",
        )}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export function SalaryForm() {
  const [role, setRole] = useState<Role>("ai-engineer");
  const [experience, setExperience] = useState<Experience>("mid");
  const [region, setRegion] = useState<Region>("london");

  const base = BASE_SALARIES[role];
  const expMultiplier = EXPERIENCE_MULTIPLIER[experience];
  const regMultiplier = REGION_MULTIPLIER[region];

  const salaryMin = roundToNearest(
    base.min * expMultiplier * regMultiplier,
    1000,
  );
  const salaryMax = roundToNearest(
    base.max * expMultiplier * regMultiplier,
    1000,
  );

  return (
    <Card>
      <CardContent className="grid gap-8 sm:grid-cols-2 sm:items-stretch sm:gap-10">
        {/* Form controls */}
        <div className="flex flex-col gap-5">
          <SelectField
            id="salary-role"
            label="Role"
            value={role}
            options={ROLES}
            onChange={setRole}
          />
          <SelectField
            id="salary-experience"
            label="Experience"
            value={experience}
            options={EXPERIENCE_LEVELS}
            onChange={setExperience}
          />
          <SelectField
            id="salary-region"
            label="Region"
            value={region}
            options={REGIONS}
            onChange={setRegion}
          />
        </div>

        {/* Result */}
        <div
          aria-live="polite"
          className="flex flex-col justify-center rounded-md border border-line bg-light-bg p-6 sm:p-8"
        >
          <span className="eyebrow">Estimated annual salary</span>
          <p className="mt-3 font-heading text-3xl font-semibold text-dark sm:text-4xl">
            {formatGBP(salaryMin)} to {formatGBP(salaryMax)}
          </p>
          <p className="mt-4 border-t border-line pt-4 text-sm leading-relaxed text-mid">
            Based on 2025/26 UK market data. Figures are indicative and vary by
            employer, sector, and individual experience.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
